// A button that opens a small panel (filters, the label picker, the header's
// menus). Closes on Escape, on a click outside, and returns focus to its
// button.
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cx } from '../transcript/bits'
import { canInteract, useTabScope } from '../workspace/context'

export interface PopoverProps {
  /** Accessible name of the panel. */
  label: string
  button: ReactNode
  /** A class for the whole: the button and its panel. */
  className?: string
  buttonClassName?: string
  /** Accessible name of the button, for one that shows no words (an icon). */
  buttonLabel?: string
  /** The button's tooltip. */
  buttonTitle?: string
  align?: 'start' | 'end'
  children: (close: () => void) => ReactNode
}

export function Popover({ label, button, className, buttonClassName, buttonLabel, buttonTitle, align = 'start', children }: PopoverProps) {
  const scope = useTabScope()
  const [open, setOpen] = useState(false)
  const [flip, setFlip] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const panelId = useId()
  // Reset only when the owning tab changes; a passive mount reset can discard
  // input that opens the panel before its effects run (Poulpe-131).
  const [scopeId, setScopeId] = useState(scope?.id)
  if (scopeId !== scope?.id) {
    setScopeId(scope?.id)
    setOpen(false)
  }

  // A panel that would leave the screen on the right opens towards the left.
  useLayoutEffect(() => {
    if (!open) {
      setFlip(false)
      return
    }
    const rect = panel.current?.getBoundingClientRect()
    if (rect && rect.right > window.innerWidth - 8) setFlip(true)
  }, [open, scope])

  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (!canInteract(scope)) return
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
        trigger.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    const first = root.current?.querySelector<HTMLElement>('.popover-panel input, .popover-panel button')
    if (canInteract(scope)) first?.focus()
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, scope])

  const close = () => {
    setOpen(false)
    trigger.current?.focus()
  }

  return (
    <div className={cx('popover', className)} ref={root}>
      <button
        ref={trigger}
        type="button"
        className={cx('button', buttonClassName, open && 'is-open')}
        aria-label={buttonLabel}
        title={buttonTitle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {button}
      </button>
      {open && (
        <div
          ref={panel}
          id={panelId}
          className={cx('popover-panel', (align === 'end' || flip) && 'align-end')}
          role="group"
          aria-label={label}
        >
          {children(close)}
        </div>
      )}
    </div>
  )
}
